import { supabase } from '../../lib/supabase'

// ─── Com'e' andata (gare di un giorno, a gara conclusa) ──────────────────
// Il punteggio si rifa' lato app con le stesse regole di fn_simulazione_punti:
//  - ogni scelta e' una previsione: lo slot k prevede il k-esimo posto; punti = dim_one_day_scores[slot][posizione d'arrivo],
//    fuori tabella (o non classificato) = default_out_of_rank (-10)
//  - bonus/malus sulla cinquina: -20 se nessuno ha vinto (miglior piazzamento > 1), +20 se tutti nei primi 10, +5 se tutti nei primi 15
export const OUT_OF_RANK = -10
const MALUS_WINNER = -20, BONUS_TOP10 = 20, BONUS_TOP15 = 5

// Distacco "00:00:12" -> "+0:12" (vuoto per il vincitore/zero)
export function fmtGap(g) {
  if (!g) return ''
  const m = String(g).match(/(?:(\d+) days? )?(\d+):(\d+):(\d+)/)
  if (!m) return String(g)
  const h = Number(m[2]) + (m[1] ? Number(m[1]) * 24 : 0), mi = Number(m[3]), se = Number(m[4])
  if (h === 0 && mi === 0 && se === 0) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return h > 0 ? `+${h}:${pad(mi)}:${pad(se)}` : `+${mi}:${pad(se)}`
}

export async function fetchOdrResults(idStage, riderIds) {
  const ids = [...new Set(riderIds)].filter(Boolean)
  const [scoresRes, resRes, winRes] = await Promise.all([
    supabase.from('dim_one_day_scores').select('slot_predicted, rank_arrival, points'),
    ids.length ? supabase.from('fact_results').select('id_rider, rank_stage').eq('id_stage', idStage).eq('is_published', true).in('id_rider', ids) : Promise.resolve({ data: [] }),
    supabase.from('fact_results').select('id_rider').eq('id_stage', idStage).eq('is_published', true).eq('rank_stage', 1).limit(1),
  ])
  const scores = {}
  ;(scoresRes.data ?? []).forEach(r => { (scores[r.slot_predicted] ??= {})[r.rank_arrival] = r.points })
  const ranks = {}
  ;(resRes.data ?? []).forEach(r => { ranks[r.id_rider] = r.rank_stage })
  const winnerId = winRes.data?.[0]?.id_rider ?? null
  return { scores, ranks, winnerId, hasResults: (resRes.data ?? []).length > 0 || winnerId !== null }
}

// Punteggio di una cinquina: picks = [{ rank, pts }] (solo corridori con risultato). Ritorna base, bonus/malus e totale.
export function oneDayScore(picks) {
  const base = picks.reduce((a, p) => a + p.pts, 0)
  const bonus = []
  if (picks.length) {
    const best = Math.min(...picks.map(p => p.rank ?? 200))
    const worst = Math.max(...picks.map(p => p.rank ?? 200))
    if (best > 1) bonus.push({ kind: 'malus', value: MALUS_WINNER })
    if (worst <= 10) bonus.push({ kind: 'top10', value: BONUS_TOP10 })
    if (worst <= 15) bonus.push({ kind: 'top15', value: BONUS_TOP15 })
  }
  return { base, bonus, total: base + bonus.reduce((a, b) => a + b.value, 0) }
}

const mean = (a) => a.reduce((x, y) => x + y, 0) / (a.length || 1)

// Corridore che conta davvero in uno slot: come view_effective_picks, il flip sostituisce il titolare solo se arriva meglio
export function makeEffective(info, flips, ranks) {
  return (r, sl) => {
    const base = info.userRiders[r.id_user]?.[sl]
    const f = flips[r.id_user]
    if (!f || f.id_slot !== sl || !f.id_rider) return base
    const rt = ranks[base] ?? null, rf = ranks[f.id_rider] ?? null
    if (rt == null && rf == null) return base
    return (rf ?? 99999) < (rt ?? 99999) ? f.id_rider : base
  }
}

export function buildResults({ rows, info, flips, results, meRow }) {
  const { scores, ranks, winnerId } = results
  const eff = makeEffective(info, flips, ranks)
  const nameOf = (id) => { const rd = info.riders[id]; return rd ? (rd.labelNew || rd.label) : '' }

  const players = rows.pivot.map(r => {
    const picks = rows.slots.map(sl => {
      const id = eff(r, sl)
      if (!id || !(id in ranks)) return null           // senza risultato pubblicato non entra nel conto (come nel DB)
      const rank = ranks[id]
      return { slot: sl, id, rank, pts: scores[sl]?.[rank] ?? OUT_OF_RANK }
    }).filter(Boolean)
    const { base, bonus, total } = oneDayScore(picks)
    return { row: r, picks, base, bonus, total }
  }).filter(p => p.picks.length)

  const me = meRow ? players.find(p => p.row === meRow) ?? null : null
  const mePicks = me ? me.picks.map(p => ({ ...p, name: nameOf(p.id) })) : []
  const myBest = mePicks.length ? [...mePicks].sort((a, b) => b.pts - a.pts)[0] : null
  const myWorst = mePicks.length > 1 ? [...mePicks].sort((a, b) => a.pts - b.pts)[0] : null

  // chi aveva il vincitore
  const winnerOwners = winnerId ? players.filter(p => p.picks.some(k => k.id === winnerId)).map(p => ({ row: p.row, slot: p.picks.find(k => k.id === winnerId).slot })) : []

  // scelta che ha fatto la differenza / affondato: confronto tra il totale medio di chi aveva il corridore e di chi no
  const riders = {}
  players.forEach(p => p.picks.forEach(k => { (riders[k.id] ??= new Set()).add(p) }))
  let difference = null, flop = null
  Object.entries(riders).forEach(([id, set]) => {
    const owners = [...set]
    const others = players.filter(p => !set.has(p))
    if (!others.length) return
    const delta = Math.round(mean(owners.map(p => p.total)) - mean(others.map(p => p.total)))
    const entry = { id: Number(id), name: nameOf(Number(id)), delta, owners: owners.map(p => p.row), rank: ranks[id] }
    if (delta > 0 && (!difference || delta > difference.delta)) difference = entry
    if (delta < 0 && owners.length >= 2 && (!flop || delta < flop.delta)) flop = entry
  })

  return {
    me: me ? { total: me.total, picks: mePicks, bonus: me.bonus, best: myBest, worst: myWorst } : null,
    winner: winnerId ? { id: winnerId, name: nameOf(winnerId), owners: winnerOwners } : null,
    difference, flop,
  }
}

// Risultati di una tappa di un giro (posizione e distacco dei corridori scelti + vincitore)
export async function fetchStageResults(idStage, riderIds) {
  const ids = [...new Set(riderIds)].filter(Boolean)
  const [resRes, winRes] = await Promise.all([
    ids.length ? supabase.from('fact_results').select('id_rider, rank_stage, gap_stage, is_dnf').eq('id_stage', idStage).eq('is_published', true).in('id_rider', ids) : Promise.resolve({ data: [] }),
    supabase.from('fact_results').select('id_rider').eq('id_stage', idStage).eq('is_published', true).eq('rank_stage', 1).limit(1),
  ])
  const results = {}
  ;(resRes.data ?? []).forEach(r => { results[r.id_rider] = { rank: r.rank_stage, gap: r.gap_stage, dnf: r.is_dnf } })
  return { results, winnerId: winRes.data?.[0]?.id_rider ?? null }
}
